/**
 * `SheetRegistries` — the lookup tables a sheet resolves its rows through:
 * row-type specs (defaults, child seeds, layout per wire type), computed-source
 * builders (which wire types are engine-computed), alias templates, chart data
 * presets, and the render-pipeline flags.
 *
 * A client carries ONE (`ClientConfig.registries`), handed to every sheet it
 * opens (`Sheet.registries`), so two clients in one process can resolve rows
 * differently. `SheetRegistries.BUILTINS` is the frozen built-in set —
 * `LonaSdk.Client.headlessScript` fills it in, and an app that adds nothing
 * passes it as is. `SheetRegistries.extend(base, overrides)` layers a
 * client's own entries over a base: an override entry wins, every other key
 * falls through to the base.
 *
 * The module-level registries behind `BUILTINS` (`ROW_TYPE_SPECS`,
 * `COMPUTED_SOURCE_BUILDERS`, `ALIAS_TEMPLATES`, the data presets) are
 * populated at module load and frozen; nothing else reads them.
 */

import type { Option } from "../core/option.ts";
import { rowTypeSpecRegistry, type RowTypeSpec } from "../rows/row-type-spec.ts";
import {
  type ComputedSourceBuilder,
  computedSourceRegistry,
} from "../rows/variants/computed-source-registry.ts";
import type { AliasTemplate } from "../aliases/alias-templates.ts";
import { RenderFlags } from "../render/render-flags.ts";
import type { DataPresetSpec } from "../rows/data-preset-spec.ts";
import { dataPresetLookupKeys, dataPresetsFor } from "../indices/data/data-presets.ts";

/**
 * The process-wide alias-template fallback: always empty. Production
 * registers nothing here — every built-in template reaches a sheet through its
 * per-sheet overlay (`BuiltinAliasFactory`, `sheet.aliasTemplates`) — and a
 * test layers its fixture templates on the client or sheet it builds
 * (`SheetRegistries.extend(base, { aliasTemplates })`), never here.
 */
export const ALIAS_TEMPLATES: SheetRegistries.Lookup<AliasTemplate> = Object.freeze({
  get(_name: string): Option<AliasTemplate> {
    return null;
  },
});

/**
 * The lookup tables a client's sheets resolve their rows through: row types,
 * computed sources, alias templates and data presets.
 */
export interface SheetRegistries {
  /** Per-wire-type row metadata: defaults, child seeds, layout (`RowTypeSpec`). */
  readonly rowTypes: SheetRegistries.Lookup<RowTypeSpec>;
  /** Wire type → the `ComputedDataSource` builder of an engine-computed row. */
  readonly computedSources: SheetRegistries.Lookup<ComputedSourceBuilder>;
  /**
   * Alias templates by name — the fallback after a sheet's own overlay
   * (`BuiltinAliasFactory`, `ClientConfig.rowsOptions`).
   */
  readonly aliasTemplates: SheetRegistries.Lookup<AliasTemplate>;
  /**
   * Chart presets by a row's lookup key, wildcard- and auth-aware
   * (`:system.host[abc].health` resolves `:system.host[*].health`).
   */
  readonly dataPresets: SheetRegistries.Lookup<readonly DataPresetSpec[]>;
  /**
   * This client's render-pipeline flags (`RenderFlags`): whether the SDK 2.0
   * pipeline feeds the live render path — the LR6 skip of every sheet it
   * opens, the binder's scene commit and cell directives — and whether this
   * is a measurement session. Read fresh at each decision.
   */
  readonly render: RenderFlags;
}

/** Types and values that go with `SheetRegistries`: `Lookup`, `BUILTINS`, `Overrides`, `extend`. */
export namespace SheetRegistries {
  /** A read-only keyed lookup: the entry for `key`, or `null`. */
  export interface Lookup<V> {
    get(key: string): Option<V>;
  }

  /** The built-in registries, frozen. */
  export const BUILTINS: SheetRegistries = Object.freeze({
    rowTypes: Object.freeze({ get: (key: string) => rowTypeSpecRegistry.get(key) }),
    computedSources: Object.freeze({ get: (key: string) => computedSourceRegistry.get(key) }),
    aliasTemplates: Object.freeze({ get: (key: string) => ALIAS_TEMPLATES.get(key) }),
    dataPresets: Object.freeze({ get: (key: string) => dataPresetsFor(key) }),
    render: RenderFlags.SHIPPED,
  });

  /** Entries a client layers over a base (`extend`); an absent table adds nothing. */
  export interface Overrides {
    readonly rowTypes?: ReadonlyMap<string, RowTypeSpec>;
    readonly computedSources?: ReadonlyMap<string, ComputedSourceBuilder>;
    readonly aliasTemplates?: ReadonlyMap<string, AliasTemplate>;
    /** Keyed like the built-ins: exact or `[*]`-wildcard lookup keys. */
    readonly dataPresets?: ReadonlyMap<string, readonly DataPresetSpec[]>;
    /** The render-pipeline flags (see `SheetRegistries.render`); a host's switchable `RenderFlagControls`. */
    readonly render?: RenderFlags;
  }

  /**
   * `base` with `overrides` consulted first, frozen. A key an override table
   * holds wins; every other key resolves through `base`.
   */
  export function extend(base: SheetRegistries, overrides: Overrides): SheetRegistries {
    return Object.freeze({
      rowTypes: layered(base.rowTypes, overrides.rowTypes ?? null, exactKey),
      computedSources: layered(base.computedSources, overrides.computedSources ?? null, exactKey),
      aliasTemplates: layered(base.aliasTemplates, overrides.aliasTemplates ?? null, exactKey),
      dataPresets: layered(base.dataPresets, overrides.dataPresets ?? null, dataPresetLookupKeys),
      render: overrides.render ?? base.render,
    });
  }

  function exactKey(key: string): readonly string[] {
    return [key];
  }

  function layered<V>(
    base: Lookup<V>,
    over: Option<ReadonlyMap<string, V>>,
    keysOf: (key: string) => readonly string[],
  ): Lookup<V> {
    if (over == null || over.size === 0) return base;
    return Object.freeze({
      get(key: string): Option<V> {
        for (const candidate of keysOf(key)) {
          const hit = over.get(candidate);
          if (hit !== undefined) return hit;
        }
        return base.get(key);
      },
    });
  }
}
